import { KeyboardEvent } from 'react';
import OrderStatusBadge from '@/components/dashboard/OrderStatusBadge';
import StatCard from '@/components/dashboard/StatCard';
import { Order } from '@/models/Order';
import { OrderVariance, OrderVarianceKind, StoreOrderVariance } from '@/models/OrderVariance';
import { OrderDetailsPath } from '@/modules/order/paths';
import { getCurrencySymbol } from '@/utils/currency';
import { CurrencyDollarIcon, HashtagIcon } from '@heroicons/react/24/outline';
import { TruckIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

interface LatestOrdersStatsProps {
  orders: Order[];
  isLoading: boolean;
  limit?: number;
  isSupplier?: boolean;
  orderVariancesByOrderId?: Map<string, StoreOrderVariance>;
}

const LatestOrdersStats = ({
  orders,
  isLoading,
  limit = 5,
  isSupplier = false,
  orderVariancesByOrderId,
}: LatestOrdersStatsProps) => {
  const navigate = useNavigate();
  const recentOrders = [...orders]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, limit);

  const openOrder = (orderId: string) => {
    navigate(OrderDetailsPath.withId(orderId));
  };

  const handleSupplierRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, orderId: string) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    openOrder(orderId);
  };

  const formatRoundedPrice = (value: number, currencyCode?: string) => {
    const amount = Math.round(value).toLocaleString();
    const symbol = currencyCode ? getCurrencySymbol(currencyCode) : undefined;

    return symbol ? `${symbol}${amount}` : amount;
  };

  const formatVarianceValue = (variance: OrderVariance) => {
    if (variance.kind === OrderVarianceKind.OrderValue) {
      return {
        label: 'Value',
        actual: formatRoundedPrice(variance.actualValue, variance.currencyCode),
        average: formatRoundedPrice(variance.averageValue, variance.currencyCode),
      };
    }

    return {
      label: 'SKUs',
      actual: `${Math.round(variance.actualValue).toLocaleString()} ${
        Math.round(variance.actualValue) === 1 ? 'SKU' : 'SKUs'
      }`,
      average: `${Math.round(variance.averageValue).toLocaleString()} ${
        Math.round(variance.averageValue) === 1 ? 'SKU' : 'SKUs'
      }`,
    };
  };

  const getVarianceIcon = (variance: OrderVariance) =>
    variance.kind === OrderVarianceKind.OrderValue ? CurrencyDollarIcon : HashtagIcon;

  const formatCreatedDate = (createdAt?: string | Date) =>
    createdAt &&
    new Date(createdAt).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const formatCreatedTime = (createdAt?: string | Date) =>
    createdAt &&
    new Date(createdAt).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const orderVarianceNotes = (orderId: string) => {
    const orderVariance = orderVariancesByOrderId?.get(orderId);
    if (!orderVariance || orderVariance.variances.length === 0) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-1.5 text-xs font-medium text-gray-600">
        {orderVariance.variances.map((variance) => {
          const value = formatVarianceValue(variance);
          const Icon = getVarianceIcon(variance);

          return (
            <div
              key={`${variance.kind}-${variance.direction}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs leading-4"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-gray-500 shadow-sm ring-1 ring-gray-200">
                <Icon className="h-3 w-3" />
              </span>
              <span className="min-w-0 truncate">
                {value.label}: {value.actual}, usually {value.average}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <StatCard
      icon={<TruckIcon className="h-8 w-8 text-slate-400 mt-1" />}
      loading={false}
      value={
        <div className="min-w-0">
          {isSupplier && (
            <div className="md:hidden">
              {isLoading &&
                Array.from({ length: limit }).map((_, i) => (
                  <div key={`mobile-loading-${i}`} className="border-b py-3 last:border-none">
                    <div className="mb-2 h-4 w-28 animate-pulse rounded bg-gray-200" />
                    <div className="mb-2 h-3 w-40 max-w-full animate-pulse rounded bg-gray-200" />
                    <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
                  </div>
                ))}

              {!isLoading &&
                recentOrders.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className="block w-full border-b py-3 text-left last:border-none"
                    onClick={() => openOrder(o.id)}
                    data-testid="dashboard-order-mobile-row"
                    data-order-id={o.id}
                    data-order-code={o.code}
                  >
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-gray-800">{o.code}</div>
                        <div className="truncate text-sm text-gray-700">{o.store?.name ?? '-'}</div>
                        {orderVarianceNotes(o.id)}
                        <div className="mt-2">
                          <OrderStatusBadge order={o} isSupplier />
                        </div>
                      </div>

                      {o.createdAt && (
                        <div className="shrink-0 text-right text-xs text-gray-500">
                          <div>{formatCreatedDate(o.createdAt)}</div>
                          <div>{formatCreatedTime(o.createdAt)}</div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}

              {!isLoading && recentOrders.length === 0 && (
                <div className="py-3 text-center text-gray-500 italic">No recent orders</div>
              )}
            </div>
          )}

          <div className={isSupplier ? 'hidden overflow-x-auto md:block' : 'overflow-x-auto'}>
            <table className={`w-full min-w-full text-sm ${isSupplier ? 'table-fixed' : ''}`}>
              <thead className="border-b text-gray-700 font-medium">
                <tr>
                  <th className={`py-2 text-left ${isSupplier ? 'w-72 pr-6' : ''}`}>Order</th>
                  <th className={`py-2 text-left ${isSupplier ? 'pl-3' : ''}`}>
                    {isSupplier ? 'Store' : 'Supplier / Store'}
                  </th>
                  <th className={`py-2 ${isSupplier ? 'w-44 text-left' : 'text-right'}`}>
                    Created
                  </th>
                  {isSupplier && (
                    <th className="hidden py-2 pl-10 text-left lg:table-cell lg:w-64 lg:!text-gray-700">
                      Status
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {isLoading &&
                  Array.from({ length: limit }).map((_, i) => (
                    <tr key={`loading-${i}`} className="border-b last:border-none">
                      <td className="py-3">
                        <div className="h-4 bg-gray-200 animate-pulse rounded w-28 mb-1" />
                        <div className="h-3 bg-gray-200 animate-pulse rounded w-16" />
                      </td>

                      <td className="py-3">
                        <div className="h-4 bg-gray-200 animate-pulse rounded w-32 mb-1" />
                        <div className="h-3 bg-gray-200 animate-pulse rounded w-24" />
                      </td>

                      <td className={`py-3 ${isSupplier ? 'text-left' : 'text-right'}`}>
                        <div className="h-4 bg-gray-200 animate-pulse rounded w-20 mb-1 ml-auto" />
                        <div className="h-3 bg-gray-200 animate-pulse rounded w-14 ml-auto" />
                      </td>

                      {isSupplier && (
                        <td className="hidden py-3 pl-10 text-left lg:table-cell">
                          <div className="h-5 bg-gray-200 animate-pulse rounded-full w-24 ml-auto" />
                        </td>
                      )}
                    </tr>
                  ))}

                {!isLoading &&
                  recentOrders.map((o) => (
                    <tr
                      key={o.id}
                      className={`border-b last:border-none ${
                        isSupplier
                          ? 'cursor-pointer transition-colors hover:bg-gray-50 focus-within:bg-gray-50'
                          : ''
                      }`}
                      onClick={isSupplier ? () => openOrder(o.id) : undefined}
                      onKeyDown={
                        isSupplier ? (event) => handleSupplierRowKeyDown(event, o.id) : undefined
                      }
                      tabIndex={isSupplier ? 0 : undefined}
                      role={isSupplier ? 'link' : undefined}
                      data-testid={isSupplier ? 'dashboard-order-row' : undefined}
                      data-order-id={isSupplier ? o.id : undefined}
                      data-order-code={isSupplier ? o.code : undefined}
                    >
                      <td className={`py-2 ${isSupplier ? 'pr-6' : ''}`}>
                        <div className="text-gray-800 font-medium">{o.code}</div>
                        {isSupplier && orderVarianceNotes(o.id)}

                        {!isSupplier && <OrderStatusBadge order={o} />}
                        {isSupplier && (
                          <div className="mt-1 lg:hidden">
                            <OrderStatusBadge order={o} isSupplier />
                          </div>
                        )}
                      </td>

                      <td className={`py-2 text-gray-700 ${isSupplier ? 'pl-3' : ''}`}>
                        {isSupplier ? (
                          <div>{o.store?.name ?? '-'}</div>
                        ) : (
                          <>
                            <div>{o.supplier?.name ?? '-'}</div>
                            <div className="text-gray-500 text-xs">{o.store?.name ?? '-'}</div>
                          </>
                        )}
                      </td>

                      <td
                        className={`py-2 text-gray-700 ${isSupplier ? 'text-left' : 'text-right'}`}
                      >
                        {o.createdAt && (
                          <>
                            <div>{formatCreatedDate(o.createdAt)}</div>
                            <div className="text-xs text-gray-500">
                              {formatCreatedTime(o.createdAt)}
                            </div>
                          </>
                        )}
                      </td>

                      {isSupplier && (
                        <td className="hidden py-2 pl-10 text-left align-middle lg:table-cell">
                          <OrderStatusBadge order={o} isSupplier />
                        </td>
                      )}
                    </tr>
                  ))}

                {!isLoading && recentOrders.length === 0 && (
                  <tr>
                    <td
                      colSpan={isSupplier ? 4 : 3}
                      className="py-3 text-center text-gray-500 italic"
                    >
                      No recent orders
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      }
    />
  );
};

export default LatestOrdersStats;
